import React, { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { SpeedInsights } from '@vercel/speed-insights/react'
import './index.css'
import { AuthProvider, useAuth, Login, ResetPassword } from './auth'
import AppShell from './AppShell.jsx'
// S3b — public no-login quote signing page (capability URL ?quote_token=<uuid>).
// Lazy so it never bloats the authed app bundle; only public signers fetch it.
import { getPublicQuoteToken } from './lib/nav/publicQuoteToken'
const PublicQuoteView = lazy(() => import('./views/PublicQuoteView.jsx'))
// TICKET-228 S3a — kanban-card QR deep-link /reorder/{materialId}. AUTHED
// (branches AFTER the login gate): staff scan → confirm qty → draft-PO line.
const ReorderScan = lazy(() => import('./components/procurement/ReorderScan.jsx'))
const REORDER_PATH_RE = /^\/reorder\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
import ErrorBoundary from './components/ErrorBoundary.jsx'
import { ThemeProvider } from './lib/ThemeContext.jsx'
import { I18nProvider } from './lib/i18n/I18nContext.jsx'
import { LookupProvider } from './lib/lookups/LookupContext.jsx'
import { ToastProvider } from './components/shared/ToastProvider.jsx'
import { AgentModeProvider } from './lib/agent/agentModeContext.jsx'
import { GLOBAL_CSS } from './styles.js'
import { initErrorReporter } from './lib/errorReporter'
import { requestPersistentStorage } from './lib/offline/persistStorage'
import { logAction } from './lib/observability'
import { initWebVitals } from './lib/observability/webVitals'

/* ── Sentry Initialization — before anything else ──────────────────── */
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || '',
  environment: import.meta.env.MODE,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
});

/* ── Global Error Handlers — catch unhandled async + sync errors ────── */
window.addEventListener('unhandledrejection', (e) => {
  console.error('[ACN] Unhandled promise rejection:', e.reason?.message || e.reason);
  Sentry.captureException(e.reason);
  // Prevent the error from propagating and killing the app
  e.preventDefault();
});
window.addEventListener('error', (e) => {
  console.error('[ACN] Uncaught error:', e.message, e.filename, e.lineno);
  Sentry.captureException(e.error || e.message);
});

/* ── Service Worker Registration ────────────────────────────────────── */
if ('serviceWorker' in navigator) {
  // TICKET-089 — deploy freshness. The CEO keeps the PWA open and checks 20+/day;
  // a network-first SW serves fresh bundles on reload but never forces one, so an
  // open session can sit on a stale in-memory bundle. We used to SILENTLY reload
  // on controllerchange, but that can yank the user mid-action. Instead, the
  // UpdateBanner component (mounted in AppShell) watches for a new build via
  // lib/pwa/swUpdate.js and shows a user-controlled "New version — Reload" prompt
  // (it also periodically re-checks so a deploy while the app is open is noticed).
  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      console.log('[ACN] SW registered:', reg.scope);
      // Proactively check for a newer SW on each app open (deploys ship a new
      // sw.js cache version); if found it activates via skipWaiting → reload.
      reg.update().catch(() => {});
    } catch (err) {
      console.warn('[ACN] SW registration failed:', err);
    }
  });
}

/* ── Durable storage — keep the offline cache + outbox from being evicted ──── */
// IndexedDB (offline read-cache + queued edits) is evictable by default; iOS can
// wipe it on a full Safari close while the SW Cache Storage survives → app loads
// but data is gone. Request persistence (granted for installed PWAs; denied in a
// plain iOS Safari tab) and log the outcome so we can see, per device, if it took.
if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.persist) {
  window.addEventListener('load', async () => {
    try {
      const result = await requestPersistentStorage();
      console.log('[ACN] persistent storage:', result);
      try { logAction('storage:persist', result); } catch { /* spine best-effort */ }
    } catch (err) {
      console.warn('[ACN] persist request failed:', err);
    }
  });
}

/* ── Auth Gate ──────────────────────────────────────────────────────── */
function AuthGate() {
  const { user, loading } = useAuth();

  // S3b — capability-URL signing page. The unguessable token in the URL IS the
  // authorization, so this branches BEFORE the auth/login gate and renders for an
  // anonymous browser regardless of session/loading. Only a UUID-shaped token
  // qualifies (getPublicQuoteToken validates); anything else falls through to the
  // normal auth flow. The page itself talks only to the pre-auth public endpoints.
  const publicQuoteToken = getPublicQuoteToken();
  if (publicQuoteToken) {
    return (
      <Suspense fallback={(
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--grad, var(--bg))' }}>
          <div style={{ width: 32, height: 32, borderRadius: 16, border: '3px solid var(--bdr)', borderTopColor: 'var(--accent)', animation: 'spin 800ms linear infinite' }} />
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
      )}>
        <PublicQuoteView token={publicQuoteToken} />
      </Suspense>
    );
  }

  // Password recovery — the emailed reset link lands at /reset-password with a
  // `type=recovery` token supabase-js turns into a short-lived recovery session. Branch
  // BEFORE the loading/login gate: that session makes `user` truthy, which would
  // otherwise drop the user into the app without ever setting a new password.
  const isRecovery = window.location.pathname === '/reset-password'
    || /(?:^|[#&?])type=recovery/.test(window.location.hash);
  if (isRecovery) return <ResetPassword />;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--grad, #15191F)',
        fontFamily: "-apple-system, 'SF Pro Display', 'Geist', sans-serif",
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, opacity: 0.6 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 16,
            // HEX_TODO[#000000] exception — see color-map.json
            border: '3px solid #1C3F75', borderTopColor: 'transparent',
            animation: 'spin 800ms linear infinite',
          }} />
          <span style={{ fontSize: 13, color: 'var(--tm)', fontWeight: 500 }}>Loading…</span>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) return <Login />;

  // QR reorder deep-link — after login so the scan lands here even when the
  // phone had to authenticate first (TICKET-228 S3a).
  const reorderMatch = window.location.pathname.match(REORDER_PATH_RE);
  if (reorderMatch) {
    return (
      <Suspense fallback={(
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--grad, var(--bg))' }}>
          <div style={{ width: 32, height: 32, borderRadius: 16, border: '3px solid var(--bdr)', borderTopColor: 'var(--accent)', animation: 'spin 800ms linear infinite' }} />
          <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
        </div>
      )}>
        <ReorderScan materialId={reorderMatch[1]} />
      </Suspense>
    );
  }

  return <AppShell />;
}

/* ── Root App with CSS injection via React tree ─────────────────────── */
function App() {
  return (
    <ErrorBoundary fullScreen viewName="App">
      <style>{GLOBAL_CSS}</style>
      <ThemeProvider>
        <I18nProvider>
          <LookupProvider>
            <ToastProvider>
              <AuthProvider>
                <AgentModeProvider>
                  <AuthGate />
                  <SpeedInsights />
                </AgentModeProvider>
              </AuthProvider>
            </ToastProvider>
          </LookupProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/* ── Error Reporter — logs to app_error_logs in Supabase ─────────────── */
initErrorReporter();

/* ── Web vitals (TTFB/FCP/LCP/INP) -> app_perf_events via /api/ops perf.ingest (TICKET-1250) ── */
initWebVitals();

/* ── Render ──────────────────────────────────────────────────────────── */
ReactDOM.createRoot(document.getElementById('root')).render(<App />)
